import Vue from "vue";
import { ActionTree, MutationTree, GetterTree } from "vuex";

const initialState: WebsocketState = {
  connection: null,
  timeout: undefined,
  interval: undefined,
  live: false,
  eventData: undefined,
};

const getters: GetterTree<WebsocketState, RootState> = {
  connection(state: WebsocketState): WebSocket | null {
    return state.connection;
  },
  isLive(state: WebsocketState): boolean {
    return state.live;
  },
  getEventData(state: WebsocketState): any {
    return state.eventData;
  },
};

const mutations: MutationTree<WebsocketState> = {
  RESET(state: WebsocketState) {
    state.connection = null;
  },
  SET_NEW_CONNECTION(state: WebsocketState, connection: WebSocket) {
    state.connection = connection;
    state.live = true;
  },
  SET_ONLINE(state: WebsocketState) {
    state.live = true;
  },
  SET_OFFLINE(state: WebsocketState) {
    state.live = false;
  },
  SET_TIMEOUT(state: WebsocketState) {
    const self = this;
    state.timeout = setTimeout(function () {
      //@ts-ignore
      self.commit("websocket/SET_OFFLINE");
    }, 5000);
  },
  SET_INTERVAL(state: WebsocketState, interval) {
    state.interval = interval;
  },
  SET_MESSAGE(state: WebsocketState, eventData) {
    if (eventData === "pong") {
      //@ts-ignore
      this.dispatch("websocket/pong");
    } else if (eventData === "The incoming message does not have a known format") {
    } else {
      state.eventData = JSON.parse(eventData);
    }
  },
};

const actions: ActionTree<WebsocketState, RootState> = {
  reset({ commit }) {
    commit("RESET");
  },

  async connect({ commit, state, dispatch }: any) {
    let base_url = process.env.VUE_APP_API_URL;

    if (base_url.includes("://")) {
      base_url = base_url.split("://")[1];
    } else {
      base_url = `${window.location.host}${process.env.VUE_APP_API_URL}`;
    }
    if (!state.connection || state.connection.readyState !== 1) {
      return new Promise(function (resolve, reject) {
        var server = new WebSocket(`ws://${base_url}/ws`);

        commit("SET_NEW_CONNECTION", server);

        server.onopen = function () {
          commit(
            "SET_INTERVAL",
            setInterval(function () {
              // CLOSED
              if (state.connection.readyState === 3) {
                state.live = false;
                dispatch("pollConnection");

                clearInterval(state.interval);
                commit("RESET");
              } else {
                state.connection.send("ping");
                commit("SET_TIMEOUT");
              }
            }, 6000),
          );

          resolve(server);
        };

        server.onerror = function (err) {
          reject(err);
        };

        server.onmessage = function (event) {
          commit("SET_MESSAGE", event.data);
        };
      });
    }
  },

  disconnect({ state }) {
    clearTimeout(state.timeout);
    clearInterval(state.interval);
    state.connection = null;
    state.live = false;
  },

  pong({ state }) {
    clearTimeout(state.timeout);
  },

  pollConnection({ dispatch }) {
    dispatch("device/readName", { skipStore: true, timeout: 80 }, { root: true })
      .then(() => {
        window.location.reload(true);
      })
      .catch(() => {
        setTimeout(() => {
          dispatch("pollConnection");
        }, 5000);
      });
  },

  subscribePortCounters({ commit, state }, portNumber: number) {
    if (!state.connection) {
      console.error("No ws:// connection was made");
      return;
    }

    state.connection.send(
      JSON.stringify({
        subscription: {
          path: `/api/ports/port/${portNumber}/counters`,
          action: "add",
          method: {
            periodic: {
              period: 3,
            },
          },
        },
      }),
    );
  },

  unSubscribePortCounters({ commit, state }, portNumber: number) {
    if (!state.connection) {
      console.error("No ws:// connection was made");
      return;
    }

    state.connection.send(
      JSON.stringify({
        subscription: {
          path: `/api/ports/port/${portNumber}/counters`,
          action: "remove",
          method: {
            periodic: {
              period: 3,
            },
          },
        },
      }),
    );
  },

  subscribeStatusPage({ commit, state }) {
    if (!state.connection) {
      console.error("No ws:// connection was made");
      return;
    }

    state.connection.send(
      JSON.stringify({
        subscription: {
          path: `/api/poe`,
          action: "add",
          method: {
            periodic: {
              period: 5,
            },
          },
        },
      }),
    );
    state.connection.send(
      JSON.stringify({
        subscription: {
          path: `/api/ports/port`,
          action: "add",
          method: "full",
        },
      }),
    );
    state.connection.send(
      JSON.stringify({
        subscription: {
          path: `/api/ports/lags/lag`,
          action: "add",
          method: "full",
        },
      }),
    );
    state.connection.send(
      JSON.stringify({
        subscription: {
          path: `/api/config/name`,
          action: "add",
          method: "full",
        },
      }),
    );
    state.connection.send(
      JSON.stringify({
        subscription: {
          path: `/api/power`,
          action: "add",
          method: "full",
        },
      }),
    );
    state.connection.send(
      JSON.stringify({
        subscription: {
          path: `/api/groups/group`,
          action: "add",
          method: "full",
        },
      }),
    );
    state.connection.send(
      JSON.stringify({
        subscription: {
          path: `/api/trunks/trunk`,
          action: "add",
          method: "full",
        },
      }),
    );
    state.connection.send(
      JSON.stringify({
        subscription: {
          path: `/api/ip_settings`,
          action: "add",
          method: "full",
        },
      }),
    );
    state.connection.send(
      JSON.stringify({
        subscription: {
          path: `/api/mdns`,
          action: "add",
          method: "full",
        },
      }),
    );
    state.connection.send(
      JSON.stringify({
        subscription: {
          path: `/api/rlinkx/ports`,
          action: "add",
          method: "full",
        },
      }),
    );
    state.connection.send(
      JSON.stringify({
        subscription: {
          path: `/api/eink/eink_cmd_buffer`,
          action: "add",
          method: "full",
        },
      }),
    );
  },

  unSubscribeStatusPage({ commit, state }) {
    //@ts-ignore
    if (!state.connection || !state.connection.readyState !== WebSocket.OPEN) {
      console.error("No ws:// connection was made");
      return;
    }

    state.connection.send(
      JSON.stringify({
        subscription: {
          path: `/api/poe`,
          action: "remove",
          method: {
            periodic: {
              period: 5,
            },
          },
        },
      }),
    );
    state.connection.send(
      JSON.stringify({
        subscription: {
          path: `/api/ports/port`,
          action: "remove",
          method: "full",
        },
      }),
    );
    state.connection.send(
      JSON.stringify({
        subscription: {
          path: `/api/ports/lags/lag`,
          action: "remove",
          method: "full",
        },
      }),
    );
    state.connection.send(
      JSON.stringify({
        subscription: {
          path: `/api/power`,
          action: "remove",
          method: "full",
        },
      }),
    );
    state.connection.send(
      JSON.stringify({
        subscription: {
          path: `/api/groups/group`,
          action: "remove",
          method: "full",
        },
      }),
    );
    state.connection.send(
      JSON.stringify({
        subscription: {
          path: `/api/trunks/trunk`,
          action: "remove",
          method: "full",
        },
      }),
    );
    state.connection.send(
      JSON.stringify({
        subscription: {
          path: `/api/ip_settings`,
          action: "remove",
          method: "full",
        },
      }),
    );
    state.connection.send(
      JSON.stringify({
        subscription: {
          path: `/api/mdns`,
          action: "remove",
          method: "full",
        },
      }),
    );
    state.connection.send(
      JSON.stringify({
        subscription: {
          path: `/api/rlinkx/ports`,
          action: "remove",
          method: "full",
        },
      }),
    );
    state.connection.send(
      JSON.stringify({
        subscription: {
          path: `/api/eink/eink_cmd_buffer`,
          action: "remove",
          method: "full",
        },
      }),
    );
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
